import { useCallback, useMemo } from 'react';

import { Flex } from '@chakra-ui/react';
import { addYears, differenceInDays, parseISO, subYears } from 'date-fns';
import { useDispatch } from 'react-redux';
import {
  setTimelinesSettings,
  useReviewTimelineSettingList,
  useTimelinesProviderTypeSettings,
} from 'reducers/reviewCreationSlice';
import { useGetTimezoneOffset } from 'screens/performanceReview/helpers';
import { getDateWithEndOfDay, getDateWithStartOfDay } from 'utils/helpers/dateHelpers';

import { Calendar2 } from 'assets/assetsV1/iconComponents';

import { convertDateToString6 } from 'constants/helper';

import { DateRangeSelectDashed } from 'uiToolkitV1/DateRange/DateRangeSelectDashed';
import { Divider } from 'uiToolkitV1/Divider';
import {
  ExpandablePaper,
  ExpandablePaperBody,
  ExpandablePaperHeader,
} from 'uiToolkitV1/ExpandablePaper/ExpandablePaper';
import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

import { Reminders } from './Reminders';

const REVIEW_RESPONSE_PROVIDER_TYPE_LABEL = {
  SELF: 'Self Review',
  MANAGER: 'Manager Review',
  PEER: 'Peer Review',
  UPWARD: 'Upward Review',
  NOMINATOR: 'Nomination',
  RATING_CALIBRATE: 'Calibration',
  DLM: 'Dotted Line Manager Review',
  PUBLISHING: 'Publishing',
};

const REVIEW_RESPONSE_PROVIDER_TYPE_WITH_REMINDER = ['SELF', 'MANAGER', 'PEER', 'UPWARD', 'DLM'];

const SingleTimelineSelection = ({ feedbackFormId, responseProviderType, displayNumber }) => {
  const responseProviderTypeSettings = useTimelinesProviderTypeSettings({
    feedbackFormId,
    responseProviderType,
  });

  const dispatch = useDispatch();
  const minDate = subYears(getDateWithStartOfDay(), 5);
  const maxDate = addYears(getDateWithEndOfDay(), 5);

  const { startDate: startDateString, endDate: endDateString, reminders } = responseProviderTypeSettings;
  const startDateObj = useMemo(() => (startDateString ? new Date(parseISO(startDateString)) : null), [startDateString]);
  const endDateObj = useMemo(() => (endDateString ? new Date(parseISO(endDateString)) : null), [endDateString]);

  const onDateRangeChange = useCallback(
    ({ startDate, endDate }) => {
      dispatch(
        setTimelinesSettings({
          feedbackFormId,
          settings: {
            responseProviderType,
            startDate: convertDateToString6(startDate),
            endDate: convertDateToString6(endDate),
          },
        })
      );
    },
    [dispatch, responseProviderType, feedbackFormId]
  );

  const onDateRangeClear = useCallback(() => {
    dispatch(
      setTimelinesSettings({
        feedbackFormId,
        settings: {
          responseProviderType,
          startDate: null,
          endDate: null,
        },
      })
    );
  }, [dispatch, responseProviderType, feedbackFormId]);

  const onReminderChange = useCallback(
    (newReminders) => {
      dispatch(
        setTimelinesSettings({
          feedbackFormId,
          settings: {
            responseProviderType,

            reminders: newReminders.map((reminder) => ({
              ...reminder,
              reviewStage: responseProviderType,
            })),
          },
        })
      );
    },
    [dispatch, responseProviderType, feedbackFormId]
  );

  const supportsReminderCreation = REVIEW_RESPONSE_PROVIDER_TYPE_WITH_REMINDER.includes(responseProviderType);

  return (
    <Flex flexDirection="column" p="10px 12px 0 12px">
      <TextBodyMedium bold>
        {displayNumber}. {REVIEW_RESPONSE_PROVIDER_TYPE_LABEL[responseProviderType]}
      </TextBodyMedium>
      <DateRangeSelectDashed
        onSelect={onDateRangeChange}
        displayTwoMonthsLayout={false}
        placeholderText="Select Duration"
        initialDateRange={{ startDate: startDateObj, endDate: endDateObj }}
        onClear={onDateRangeClear}
        minDate={minDate}
        maxDate={maxDate}
        displayCancelButton={false}
      />
      {startDateString && endDateString && (
        <TextLabel color="text.g3" mt={1} mb={3}>
          {differenceInDays(endDateObj, startDateObj) + 1} Days total duration
        </TextLabel>
      )}
      {supportsReminderCreation && <Reminders remindersSettings={reminders ?? []} onChange={onReminderChange} />}
    </Flex>
  );
};

const TimelinesSelection = ({ feedbackFormId }) => {
  const reviewTimelineSettingList = useReviewTimelineSettingList({ feedbackFormId });
  const formattedOffset = useGetTimezoneOffset();

  return (
    <ExpandablePaper>
      <ExpandablePaperHeader
        title="Set the timelines for all stages of the review cycle"
        leftIconComponent={<Calendar2 boxSize="24px" color="icon.g2" />}
        subTitle={`Note: The start and end time for the reviewer submissions follows the Organisation's time zone ${formattedOffset}.
        Hence, all forms open at 00:00 hrs of the start date and all forms close at 23:59 hrs of the end date of that time zone.`}
      />
      <ExpandablePaperBody>
        {reviewTimelineSettingList.map(({ responseProviderType }, index) => (
          <>
            <SingleTimelineSelection
              responseProviderType={responseProviderType}
              displayNumber={index + 1}
              feedbackFormId={feedbackFormId}
            />
            {index < reviewTimelineSettingList.length - 1 && <Divider my={3} />}
          </>
        ))}
      </ExpandablePaperBody>
    </ExpandablePaper>
  );
};

export { TimelinesSelection };
